import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export interface Vendor {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  // DB returns snake_case, frontend uses camelCase
  businessId?: number;
  business_id?: number;
  address: string | null;
  contactPerson?: string | null;
  contact_name?: string | null;
  taxId?: string | null;
  tax_id?: string | null;
  isActive?: boolean;
  is_active?: boolean;
  // Extended fields used by UI forms (may not exist in DB)
  paymentTerms?: string | null;
  notes?: string | null;
  status?: string;
  category?: string | null;
  track1099?: boolean;
  form1099Type?: string | null;
  createdAt?: Date;
  created_at?: string;
  updatedAt?: Date;
  updated_at?: string;
}

export function useVendors(businessId?: number) {
  return useQuery<Vendor[]>({
    queryKey: ["/api/businesses", businessId, "vendors"],
    queryFn: async () => {
      if (!businessId) return [];
      const response = await apiRequest("GET", `/api/businesses/${businessId}/vendors`);
      return response.json();
    },
    enabled: !!businessId,
  });
}

export function useCreateVendor() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (vendor: any) => {
      const businessId = vendor.businessId;
      if (!businessId) throw new Error("Business ID is required");
      const { businessId: _, business_id: __, ...vendorPayload } = vendor;
      const response = await apiRequest("POST", `/api/businesses/${businessId}/vendors`, vendorPayload);
      return response.json();
    },
    onSuccess: (_data, variables) => {
      toast({ title: "Success", description: "Vendor created successfully" });
      queryClient.invalidateQueries({ queryKey: ["/api/businesses", variables.businessId, "vendors"] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

export function useUpdateVendor() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, businessId, business_id, ...data }: { id: number; businessId?: number; business_id?: number } & Partial<Vendor>) => {
      const bizId = businessId || business_id;
      if (!bizId) throw new Error("Business ID is required");
      const response = await apiRequest("PATCH", `/api/businesses/${bizId}/vendors/${id}`, data);
      return response.json();
    },
    onSuccess: (_data, variables) => {
      toast({ title: "Success", description: "Vendor updated successfully" });
      queryClient.invalidateQueries({ queryKey: ["/api/businesses", variables.businessId || variables.business_id, "vendors"] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

export function useVendor(businessId: number, id: number) {
  return useQuery<Vendor>({
    queryKey: ["/api/businesses", businessId, "vendors", id],
    queryFn: async () => {
      if (!id || !businessId) return null as any;
      const response = await apiRequest("GET", `/api/businesses/${businessId}/vendors/${id}`);
      return response.json();
    },
    enabled: !!id && !!businessId,
  });
}

export function use1099Vendors(businessId: number) {
  // Reuse the main vendors query and filter client-side
  const { data: vendors, ...rest } = useVendors(businessId);
  return {
    ...rest,
    data: (vendors || []).filter((v) => (v as any).track1099),
  };
}
